import useButtonLoader from 'hooks/useButtonLoader';
import moment from 'moment';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { IoIosAddCircleOutline, IoMdCloseCircleOutline } from 'react-icons/io';
import ProductList from './ProductList';
import { userPlaceholder } from 'assets/icons';
import Form from './Form';
import { toastMessage } from 'utils/toastMessage';
import { addRefillProductApi, updateRefillProductApi } from 'services/user';
import { SearchBar } from 'components/form';

// The detail API returns prescription images as FULL S3 URLs
// (e.g. "https://sindhi-bucket.s3.../sindhi/images/123-abc.png"), but the
// add/update payload must carry ONLY the relative object key/name
// (e.g. "sindhi/images/123-abc.png"). This strips the "https://<host>/" part
// (if present) and decodes %20 etc. so the name matches how it's stored.
// A freshly uploaded image already comes back as a relative path and passes
// through unchanged.
const toImageKey = (value) => {
    if (!value || typeof value !== 'string') return value;
    const match = value.match(/^https?:\/\/[^/]+\/(.+)$/i);
    const path = match ? match[1] : value;
    try {
        return decodeURIComponent(path);
    } catch {
        return path;
    }
};

const getSelectedVariant = (item) => {
    const product = item?.product || item;
    const variantId = item?.variant?._id || item?.variantId || item?.productVariantId;
    if (item?.variant) return item.variant;
    if (Array.isArray(product?.variant)) {
        return product.variant.find((variant) => variant?._id === variantId) || product.variant[0];
    }
    return product?.variant || {};
};

const getInitialProducts = (products = []) => {
    return products.map((item) => {
        const product = item?.product || item;
        return {
            ...product,
            variant: getSelectedVariant(item),
            itemType: item?.itemType || 'STRIP',
            quantity: item?.quantity || 1,
        };
    });
};

const getInitialInput = (order = {}) => ({
    startDate: order?.startDate ? moment(order.startDate).format('MM/DD/YYYY') : "",
    patientName: order?.patientName || '',
    refillCycle: order?.refillCycle || "",
    address: order?.address || order?.addressDetails?._id || '',
});

const getSearchableProductText = (product = {}) => {
    const variant = product?.variant || {};
    return [
        product?.name,
        product?.type,
        product?.description,
        variant?.unitValue,
        variant?.unit,
        variant?.quantity,
    ].filter(Boolean).join(' ').toLowerCase();
};

const formatAmount = (amount) => {
    const value = Number(amount);
    return Number.isFinite(value) ? `₹${value.toFixed(value % 1 === 0 ? 0 : 2)}` : 'N/A';
};

const getVariantPrice = (variant = {}) => {
    const finalAmount = Number(variant?.finalAmount);
    if (Number.isFinite(finalAmount) && finalAmount > 0) return finalAmount;
    return variant?.mrp;
};

const getVariantDetails = (variant = {}) => {
    const details = [
        variant?.unitValue,
        variant?.unit,
        variant?.quantity ? `(${variant.quantity})` : '',
    ].filter(Boolean);
    return details.length ? details.join(' ') : 'N/A';
};

const AddRefillOrder = ({
    userId,
    refillList = () => { },
    setAppFormOpen = () => { },
    editOrder = null,
    onCancelEdit = () => { },
    editMode = 'order',
}) => {
    const fileInputRef = useRef(null);
    const isEditMode = Boolean(editOrder?._id);
    const isMedicineEditMode = editMode === 'medicine';

    const [imgInputState, setImageInputState] = useState(editOrder?.image || []);
    const [error, setError] = useState({});
    const [buttonLoader, setButtonLoader] = useButtonLoader(isEditMode ? "Update" : "Schedule");
    const [addProduct, setAddProduct] = useState(getInitialProducts(editOrder?.products || []));
    const [productModal, setProductModal] = useState(false);
    const [input, setInput] = useState(getInitialInput(editOrder));
    const [searchText, setSearchText] = useState('');

    const filteredProducts = useMemo(() => {
        const searchValue = searchText.trim().toLowerCase();
        if (!searchValue) return addProduct;
        return addProduct.filter((product) =>
            getSearchableProductText(product).includes(searchValue)
        );
    }, [addProduct, searchText]);

    const prescriptionRequired = useMemo(() => (
        addProduct.some((product) => product?.prescriptionRequired === true)
    ), [addProduct]);

    // clear the "add at least one medicine" error as soon as a product is added
    useEffect(() => {
        if (addProduct.length > 0 && error?.product) {
            setError((prev) => {
                const { product, ...rest } = prev;
                return rest;
            });
        }
    }, [addProduct]);

    useEffect(() => {
        setImageInputState(editOrder?.image || []);
        setAddProduct(getInitialProducts(editOrder?.products || []));
        setInput(getInitialInput(editOrder));
        setSearchText('');
        setError({});
        if (isMedicineEditMode && editOrder?.status === 'PENDING') {
            setProductModal(true);
        }
    }, [editOrder, isMedicineEditMode]);

    const closeForm = () => {
        setInput(getInitialInput());
        setImageInputState([]);
        setAddProduct([]);
        setSearchText('');
        setError({});
        setAppFormOpen(false);
        onCancelEdit();
    };

    const handleDivClick = () => {
        fileInputRef.current.click();
    };

    // products are matched on product id + variant id, because a variant id can
    // be missing/duplicated on orders coming back from the detail API
    // the cards are rendered from `addProduct` itself, so the exact object
    // reference identifies the line; the id check is only a fallback for when
    // the item got re-created (e.g. after a quantity update)
    const isSameLine = (item, product) => (
        item === product ||
        (item?._id === product?._id && item?.variant?._id === product?.variant?._id)
    );

    const handleRemoveProduct = (product) => {
        setAddProduct((prev) => prev.filter((item) => !isSameLine(item, product)));
    };

    // ---- increment / decrement quantity per product card ----
    const handleQuantityChange = (product, type) => {
        setAddProduct((prev) =>
            prev.reduce((updatedProducts, item) => {
                if (!isSameLine(item, product)) {
                    return [...updatedProducts, item];
                }

                const current = Number(item?.quantity) || 1;
                const updated = type === 'inc' ? current + 1 : current - 1;

                if (updated <= 0) {
                    return updatedProducts;
                }

                return [...updatedProducts, { ...item, quantity: updated }];
            }, [])
        );
    };

    // ---- switch a line between STRIP and LOOSE while editing ----
    const handleItemTypeChange = (product, itemType) => {
        setAddProduct((prev) =>
            prev.map((item) => {
                if (!isSameLine(item, product)) return item;
                return { ...item, itemType, quantity: Number(item?.quantity) || 1 };
            })
        );
    };

    const validation = () => {
        let status = true;
        let errorMessage = {};

        // ---- at least one medicine/product must be added, in every mode ----
        if (addProduct.length === 0) {
            errorMessage['product'] = 'Please add at least one medicine';
            // toastMessage('Please add at least one medicine', 'error', 'selectProduct');
            status = false;
        }

        if (isMedicineEditMode) {
            setError(errorMessage);
            return status;
        }

        if (!input?.startDate) {
            errorMessage['startDate'] = 'Please select start date';
            status = false;
        }
        if (!input?.patientName) {
            errorMessage['patientName'] = 'Please enter patient Name';
            status = false;
        }
        if (!input?.refillCycle) {
            errorMessage['refillCycle'] = 'Please enter refill Cycle';
            status = false;
        }
        if (!input?.address) {
            errorMessage['address'] = 'Please select address';
            status = false;
        } else if (prescriptionRequired && imgInputState.length === 0) {
            toastMessage('Please Attach Medical Prescriptions', 'error', 'imageSelect');
            status = false;
        }

        setError(errorMessage);
        return status;
    };

    const appRefillProduct = async () => {
        if (validation()) {
            try {
                setButtonLoader(true);

                const payload = {
                    userId,
                    ...(isEditMode && { reFillOrderId: editOrder?._id }),
                    ...input,
                    // send only the relative image key/name, never the full S3 URL
                    image: imgInputState.map(toImageKey).filter(Boolean),
                    // each product carries its own itemType + quantity
                    items: addProduct.map((data) => ({
                        productId: data?._id,
                        variantId: data?.variant?._id,
                        itemType: data?.itemType || 'STRIP',
                        quantity: Number(data?.quantity) || 1,
                    })),
                };

                const res = isEditMode
                    ? await updateRefillProductApi(payload)
                    : await addRefillProductApi(payload);

                const { code } = res?.data;
                if (code === 200) {
                    toastMessage('Refill Order Scheduled Successfully', 'success', 'refillOrder');
                    refillList();
                    closeForm();
                }
            } catch (error) {
                console.log("Error:", error);
            } finally {
                setButtonLoader(false);
            }
        }
    };

    return (
        <>
            <div className="tabs-container d-flex justify-content-center mt-4">
                <div className="tabs-box d-flex">
                    <div className='tab-item' onClick={() => setProductModal(true)}>
                        <IoIosAddCircleOutline className='fs-2 mx-3' />
                        Add Products/Medicine
                    </div>
                </div>
            </div>

            {addProduct?.length > 0 && (
                <div className="search_wrapper-option-selector">
                    <SearchBar
                        placeholder="Search selected products..."
                        onChange={(e) => setSearchText(e.target.value)}
                        value={searchText}
                    />
                </div>
            )}

            {/* ---- product cards ---- */}
            <div className="card-container">
                {filteredProducts.length > 0 && filteredProducts.map((data, ind) => (
                    <div className="omega-card" key={data?.variant?._id || data?._id || ind}>

                        {/* remove button — available while adding and while editing */}
                        <div
                            className="close-button"
                            title="Remove medicine"
                            onClick={() => handleRemoveProduct(data)}
                        >
                            <IoMdCloseCircleOutline size={18} />
                        </div>

                        {/* image */}
                        <div className="product-image">
                            <img
                                src={data?.image?.[0] || userPlaceholder}
                                alt={data?.name}
                            />
                        </div>

                        {/* name */}
                        <div className="product-title">{data?.name}</div>

                        {/* variant details */}
                        <div className="product-title">
                            {getVariantDetails(data?.variant)}
                        </div>

                        {/* price */}
                        <div className="product-title">
                            Price: {formatAmount(getVariantPrice(data?.variant))}
                        </div>

                        {/* ---- Strip / Loose badge ---- */}
                        <div className="product-title">
                            <span
                                className={`badge ${data?.itemType === 'LOOSE' ? 'bg-warning text-dark' : 'bg-primary'}`}
                            >
                                {data?.itemType || 'STRIP'} 
                            </span>
                        </div>

                        {/* ---- quantity increment / decrement (always editable, even in edit mode) ---- */}
                        <div className="d-flex align-items-center justify-content-center gap-2 mt-2">
                            <button
                                type="button"
                                className="btn btn-outline-secondary btn-sm"
                                onClick={() => handleQuantityChange(data, 'dec')}
                            >
                                −
                            </button>

                            <span className="fw-bold px-2">{data?.quantity || 1}</span>

                            <button
                                type="button"
                                className="btn btn-outline-secondary btn-sm"
                                onClick={() => handleQuantityChange(data, 'inc')} 
                            >
                                +
                            </button>
                        </div>

                    </div>
                ))}

                {addProduct?.length > 0 && filteredProducts.length === 0 && (
                    <div className="w-100 text-center py-3">No selected products found</div>
                )}

                {addProduct?.length === 0 && error?.product && (
                    <div className="w-100 text-center py-3 text-danger">
                        {error.product}
                    </div>
                )}
            </div>

            {!isMedicineEditMode && (
                <>
                    <div className="tabs-container d-flex justify-content-center mb-4">
                        <div className="tabs-box d-flex">
                            <div className='tab-item' onClick={handleDivClick}>
                                <IoIosAddCircleOutline className='fs-2 mx-3' />
                                {prescriptionRequired
                                    ? "Attach Medical Prescriptions *"
                                    : "Attach Medical Prescriptions (Optional)"}
                            </div>
                        </div>
                    </div>

                    <Form
                        {...{ fileInputRef, input, setInput, error, setImageInputState, userId }}
                        initialImages={imgInputState}
                        prescriptionRequired={prescriptionRequired}
                    />
                </>
            )}

            <div className="form_field_wrap">
                <div className="form_field mt-2">
                    <div className="submit-button-holder mt-4">
                        <button
                            type="button"
                            className="button light me-2"
                            onClick={closeForm}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="button"
                            ref={buttonLoader}
                            onClick={appRefillProduct}
                        >
                            {isEditMode ? 'Update' : 'Schedule'}
                        </button>
                    </div>
                </div>
            </div>

            {/* product list modal */}
            <ProductList
                show={productModal}
                close={() => setProductModal(false)}
                userId={userId}
                addProduct={addProduct}
                setAddProduct={setAddProduct}
            />
        </>
    );
};

export default AddRefillOrder;
